/** @jsxImportSource @emotion/react */
'use memo'

// web/src/features/event/components/EventLocationCard.tsx
import { lazy, Suspense } from 'react'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { SnEvent } from '@sportninja/common/features/event/types'
import {
  formatEventLocation,
  formatEventStreetAddress,
} from '@sportninja/common/features/event/formatEventLocation'
import Icon from 'src/components/Icon'
import LoadingSpinner from 'src/components/LoadingSpinner'
import { font, paragraphXSmall, weightSemiBold } from 'src/components/css'
import {
  DetailRow,
  cardStyle,
  sectionLabelStyle,
  valueStyle,
} from './eventCardStyles'

/* Leaflet is heavy and only some events have coordinates — load it on demand,
   matching GameLocationCard. */
const Map = lazy(() => import('src/components/MapCard/Map'))

interface EventLocationCardProps {
  event: SnEvent
}

/**
 * Where the event happens — map, address, and a directions link.
 *
 * Split out of the details card so the page can put location on the right and
 * everything else on the left. Renders nothing when there is no location to
 * show; `EventDetailBody` uses that to decide the column layout.
 */
const EventLocationCard: React.FC<EventLocationCardProps> = ({ event }) => {
  // `location` drives the map/directions query (the best single string we
  // have). The two ROWS below are separate: the name on its own, then the
  // full street address — so a venue name never hides the address.
  const location = formatEventLocation(event)
  const streetAddress = formatEventStreetAddress(event)
  // The NAME row. `formatEventLocation` resolves the venue
  // ("{venue} - {facility}") first and the captured `address_name` second —
  // reading `address_name` directly left this blank for a venue-backed event,
  // which has none.
  //
  // Only rendered when there is ALSO a street below it. The two rows would
  // otherwise print the same string twice: with no street the Address row
  // falls back to the name, and with no name `formatEventLocation` itself
  // falls back to the street. Either way one row is the whole location.
  const locationName =
    streetAddress && location !== streetAddress ? location : ''

  // Backend geocodes the address at create time. Both must be present and
  // numeric — a partial pair would put the marker in the ocean.
  // Venue-backed events carry coords on the venue's address, same place the
  // street comes from; custom addresses carry them on the event's own.
  const geocoded = event.venue?.address ?? event.address
  const latitude = geocoded?.latitude
  const longitude = geocoded?.longitude
  const coords =
    typeof latitude === 'number' && typeof longitude === 'number'
      ? ([latitude, longitude] as [number, number])
      : null

  const handleGetDirections = () => {
    if (!location) {
      return
    }
    // Search by address rather than coords so the destination shows a name.
    const query = encodeURIComponent(location)
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${query}`,
      '_blank',
      'noopener,noreferrer'
    )
  }

  return (
    <section>
      <h2 css={sectionLabelStyle}>{capitalizeWords('Location')}</h2>
      <div css={cardStyle}>
        {coords && (
          <div css={mapWrapperStyle}>
            <Suspense fallback={<LoadingSpinner size={2} />}>
              <Map
                position={coords}
                zoom={15}
                scrollWheelZoom={false}
                zoomControl={false}
                css={mapStyle}
              />
            </Suspense>
            {/* Overlaid rather than stacked, so the map keeps its full height
                and the card does not grow a second control row. */}
            <button
              type='button'
              onClick={handleGetDirections}
              css={directionsButtonStyle}
            >
              <Icon name='map-marker-alt' fontSize={12} />
              Get Directions
            </button>
          </div>
        )}

        {locationName && (
          <DetailRow iconName='building' label='Location'>
            <span css={valueStyle}>{locationName}</span>
          </DetailRow>
        )}

        <DetailRow iconName='map-marker-alt' label='Address'>
          <span css={valueStyle}>
            {/* Falls back to `location` (the NAME), not `locationName` —
                the latter is deliberately blank when there is no street, so
                using it here dropped the name entirely and printed 'Not set'
                for a venue that has no address of its own. */}
            {streetAddress || location || 'Not set'}
          </span>
        </DetailRow>
      </div>
    </section>
  )
}

export default EventLocationCard

// ---- styled ----

/* Map sits at the top of the card, full-bleed to its rounded corners — it is
   the one element that makes the panel feel like a place rather than a form. */
const mapWrapperStyle = css`
  position: relative;
  width: 100%;
  height: 180px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${colors.SOFT_STEEL};
`

const mapStyle = css`
  width: 100%;
  height: 100%;

  /* react-leaflet ships z-indexes in the thousands; the app's own stack starts
     at 1000 (see components/css.js), so neutralize them or the map floats over
     modals and dropdowns. */
  .leaflet-pane,
  .leaflet-top,
  .leaflet-control {
    z-index: unset;
  }

  /* Attribution bar is illegible at this size and steals vertical space. */
  .leaflet-bottom {
    display: none;
  }
`

const directionsButtonStyle = css`
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 400;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid ${colors.SOFT_STEEL};
  border-radius: 6px;
  background: ${colors.SECONDARY_300};
  color: ${colors.PRIMARY};
  cursor: pointer;
  ${font.body}
  ${paragraphXSmall}
  ${weightSemiBold}
  transition: filter 0.2s ease-in-out;

  &:hover {
    filter: brightness(1.2);
  }
`
